import React, { useState } from 'react';

import { dot } from 'dot-object';

import { useForm, FormProvider } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { Link, useHistory } from 'react-router-dom';

import Alert from 'reactstrap/lib/Alert';
import Button from 'reactstrap/lib/Button';
import Col from 'reactstrap/lib/Col';
import Container from 'reactstrap/lib/Container';
import FormGroup from 'reactstrap/lib/FormGroup';
import Row from 'reactstrap/lib/Row';
import Spinner from 'reactstrap/lib/Spinner';

import { checkout } from '@ttstr/actions';
import { AppState } from '@ttstr/reducers';
import { useActions, useShallowEqualSelector } from '@ttstr/utils';

import FormField from '../Form/FormField';
import ValidationAlert from '../Form/ValidationAlert';
import LoadingSpinner from '../Loading/LoadingSpinner';
import CartListing from '../ShoppingCart/CartListing';
import CartShippingAddress from '../ShoppingCart/CartShippingAddress';
import { BackendError } from '@ttstr/api';
import ConfirmErrorMessage from './ConfirmErrorMessage';

interface ConfirmInformation {
  allow_promotion: boolean;
  tnc: boolean;
}

const Confirm: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { checkout } = useActions(mapDispatchToProps);
  const { cart, loading, cartReceived } = useShallowEqualSelector(mapStateToProps);
  const isCartValid = Boolean(cart?.valid && !cart?.no_checkout_message);
  const cartErrors = cart?.errors ? Object.entries(dot(cart.errors)) : [];
  const showAllowPromotion = cart?.newsletter.ask;
  const formContext = useForm<ConfirmInformation>({
    mode: 'onChange',
  });
  const { register, handleSubmit, formState } = formContext;
  const { isValid, isSubmitting } = formState;
  const [errorCode, setErrorCode] = useState(null);

  if (!cartReceived && loading) return <LoadingSpinner label={t(`LOADING.CART`)} />;

  const onSubmit = async (values: ConfirmInformation) => {
    const { allow_promotion } = values;
    try {
      const cartToken = await checkout(allow_promotion);
      history.push(`/cart/${cartToken}/status`);
    } catch (error) {
      if (error instanceof BackendError) {
        setErrorCode(error.message);
      } else {
        throw error;
      }
    }

  };

  return (
    <Container className="checkout-confirm mb-5">
      <Row>
        <Col lg="6">
          <div className="checkout-confirm-address">
            <FormGroup tag="fieldset">
              <legend className="legend--confirm">{t(`CHECKOUT.CONFIRM`)}</legend>
              <CartListing readOnly />
            </FormGroup>
            <FormGroup tag="fieldset" className="mb-0">
              <legend>
                <Button className="float-right" color="secondary" size="sm" tag={Link} to="/checkout#shipment-form">
                  {t('CHECKOUT.SHIPPING_METHOD_EDIT')}
                </Button>
                {t(`CUSTOMER.SHIPPING_ADDRESS`)}
              </legend>
              <CartShippingAddress />
            </FormGroup>
          </div>
        </Col>
        <Col lg="6">
          <FormProvider {...formContext}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <fieldset>
                <FormGroup tag="fieldset" className="mb-0">
                  <legend className="legend--buy">{t(`CHECKOUT.BUY`)}</legend>

                  {showAllowPromotion && (
                    <FormField
                      type="checkbox"
                      name="allow_promotion"
                      id="allow_promotion"
                      // onChange={() => setFieldValue('allow_promotion', !values.allow_promotion)}
                      label={
                        cart?.newsletter.default || !cart?.newsletter.question
                          ? t(`CHECKOUT.ALLOW_PROMOTION`)
                          : cart.newsletter.question
                      }
                      optional
                      innerRef={register}
                    />
                  )}
                  {/* eslint-disable react/jsx-no-literals */}
                  <FormField
                    type="checkbox"
                    name="tnc"
                    id="tnc"
                    label={
                      <>
                        <Trans i18nKey="CHECKOUT.TNC">
                          I hereby confirm that I have read and accept the{' '}
                          <Link to="/privacy" target="_blank">
                            Privacy Statement
                          </Link>
                          . I also confirm that I have read and accepted the{' '}
                          <Link to="/terms" target="_blank">
                            Terms and Conditions
                          </Link>
                          .{' '}
                        </Trans>
                        {Array.isArray(cart.extra_tncs) && cart.extra_tncs.length > 0 && (
                          <Trans i18nKey="CHECKOUT.TNC_EXTRA" className="d-block">
                            Additionally I accept the following Tentative Nonconfirmations:
                          </Trans>
                        )}
                        {Array.isArray(cart.extra_tncs) &&
                          cart.extra_tncs.map((tnc) => (
                            <Link key={tnc.id} to={`/agb/${tnc.id}`} target="_blank" className="d-block">
                              {tnc.title}
                            </Link>
                          ))}
                      </>
                    }
                    innerRef={register({ required: true })}
                  />
                  {/* eslint-enable react/jsx-no-literals */}

                  {Boolean(cartErrors?.length) && (
                    <Alert color="danger">
                      <h4 className="alert-heading">{t('CHECKOUT.ATTENTION')}</h4>
                      <p>{t('CHECKOUT.UNRESOLVED_ERRORS_MESSAGE')}</p>
                      <dl className="mb-0">
                        {cartErrors.map(([field, message]) => (
                          <React.Fragment key={field}>
                            <dt>{t(`CUSTOMER.${field.toUpperCase()}`)}</dt>
                            <dd>{message}</dd>
                          </React.Fragment>
                        ))}
                      </dl>
                    </Alert>
                  )}
                  <div className="d-flex flex-column justify-content-md-between align-items-md-center">
                    <Button
                      id="confirm-purchase-button"
                      className="mb-2"
                      type="submit"
                      size="lg"
                      block
                      color="primary"
                      disabled={!isCartValid || !isValid || isSubmitting}
                    >
                      <span className="d-flex justify-content-center align-items-center">
                        {t('CHECKOUT.BUY_NOW')}
                        <Spinner color="light" size="sm" className={isSubmitting ? 'ml-2 d-inline-block' : 'd-none'} />
                      </span>
                    </Button>
                    <ValidationAlert className="mb-2" externalAlertMessage={cart.no_checkout_message} />
                    <ConfirmErrorMessage errorCode={errorCode}/>
                  </div>
                </FormGroup>
              </fieldset>
            </form>
          </FormProvider>
        </Col>
      </Row>
    </Container>
  );
};

const mapStateToProps = (state: AppState) => {
  const { cart, loading, cartReceived } = state.Cart;
  return {
    cart,
    loading,
    cartReceived,
  };
};

const mapDispatchToProps = {
  checkout,
};
export default React.memo(Confirm);
